import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { InlineFailure } from '@/components/FailureNotice';
import { presentFailure } from '@/lib/api/failure';
import { SUPPORTED_LOCALES } from '@/lib/i18n';
import { useCreateWorkspace, type Workspace } from './useWorkspace';

/**
 * Mirrors `CreateWorkspaceRequest` for fast feedback; the server re-validates
 * the locale and the IANA zone and its refusal is always shown.
 */
const schema = z.object({
  name: z.string().trim().min(1).max(200),
  default_locale: z.enum(SUPPORTED_LOCALES),
  timezone: z
    .string()
    .trim()
    .min(1)
    .refine((zone) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: zone });
        return true;
      } catch {
        return false;
      }
    }),
});

type FormValues = z.infer<typeof schema>;

function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Creates a workspace. It starts at Draft; the form says so rather than offering a level. */
export function CreateWorkspaceForm({
  onCreated,
}: {
  readonly onCreated: (workspace: Workspace) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const create = useCreateWorkspace();
  const failure = presentFailure(create.error);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', default_locale: 'en-CA', timezone: browserZone() },
  });

  return (
    <form
      className="flex flex-col gap-3"
      aria-label={t('workspaces.create.heading')}
      noValidate
      onSubmit={handleSubmit((values) => create.mutate(values, { onSuccess: onCreated }))}
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-name`}>{t('workspaces.create.name')}</Label>
        <Input
          id={`${id}-name`}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={errors.name ? `${id}-name-error` : undefined}
          {...register('name')}
        />
        {errors.name ? (
          <p id={`${id}-name-error`} className="text-xs text-red-700 dark:text-red-400">
            {t('workspaces.create.nameRequired')}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-locale`}>{t('portfolio.defaultLocale')}</Label>
        <Select id={`${id}-locale`} {...register('default_locale')}>
          {SUPPORTED_LOCALES.map((locale) => (
            <option key={locale} value={locale}>
              {locale}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-tz`}>{t('settings.general.timezone')}</Label>
        <Input
          id={`${id}-tz`}
          aria-invalid={errors.timezone ? true : undefined}
          aria-describedby={`${id}-tz-hint`}
          {...register('timezone')}
        />
        <p id={`${id}-tz-hint`} className="text-xs text-muted-foreground">
          {errors.timezone
            ? t('workspaces.create.timezoneInvalid')
            : t('workspaces.create.timezoneHint')}
        </p>
      </div>
      <p className="text-sm text-muted-foreground">{t('workspaces.create.startsAtDraft')}</p>
      {failure ? <InlineFailure failure={failure} /> : null}
      <Button type="submit" className="self-start" disabled={create.isPending}>
        {t('workspaces.create.submit')}
      </Button>
    </form>
  );
}
